/**
 * Print orders and the consumable per-label print queue.
 * Shared by the print-order service, route schemas, and the client print flows.
 */

import type { LabelType } from './label.types';

/** What kind of physical label a print order produces. */
export const PRINT_LABEL_KINDS = [
  'warehouse',
  'big',
  'shelf',
  'reprint_warehouse',
  'reprint_big',
  'ship_label',
  'order_qr',
] as const;
export type PrintLabelKind = (typeof PRINT_LABEL_KINDS)[number];

/** Label kinds surfaced in the Warehouse Labels → Print Log list. */
export const WAREHOUSE_PRINT_LABEL_KINDS = [
  'warehouse',
  'big',
  'shelf',
  'reprint_warehouse',
  'reprint_big',
] as const satisfies readonly PrintLabelKind[];

/** Where in the UI the print was triggered from. */
export const PRINT_ORDER_SOURCES = [
  'print_page',
  'receiving_modal',
  'reprint_panel',
  'reprint_list',
  'ship_orders',
] as const;
export type PrintOrderSource = (typeof PRINT_ORDER_SOURCES)[number];

export const PRINT_ORDER_STATUSES = ['pending', 'completed', 'failed', 'cancelled'] as const;
export type PrintOrderStatus = (typeof PRINT_ORDER_STATUSES)[number];

export const PRINT_QUEUE_ITEM_STATUSES = ['queued', 'printed', 'failed', 'cancelled'] as const;
export type PrintQueueItemStatus = (typeof PRINT_QUEUE_ITEM_STATUSES)[number];

/**
 * Why a print order stopped. Set on the order and on the failed queue item.
 * - `print_not_configured` — PRINT_SERVICE_URL / PRINT_SERVICE_API_KEY unset
 * - `relay_unreachable`    — network error reaching the relay (ECONNREFUSED / ENOTFOUND / timeout)
 * - `relay_error`          — relay answered with an error (printer offline, TCP write failed)
 * - `printer_not_ready`    — the printer itself reported it could not print, or could not confirm
 *                            the label came out (media out, head open, paused, batch not drained)
 * - `ack_exhausted`        — client retried the order PRINT_ACK_MAX_ATTEMPTS times without `printed: true`
 * - `ack_timeout`          — client saw no response within PRINT_ACK_TIMEOUT_MS
 * - `db_error`             — storage_id reserve or shelf_indexes insert failed
 * - `validation_error`     — payload rejected (unknown SKU / shelf / bad label type)
 * - `abandoned`            — order left pending; the tab closed mid-order
 * - `cancelled_by_user`    — user stopped the order
 */
export const PRINT_FAILURE_CODES = [
  'print_not_configured',
  'relay_unreachable',
  'relay_error',
  'printer_not_ready',
  'ack_exhausted',
  'ack_timeout',
  'db_error',
  'validation_error',
  'abandoned',
  'cancelled_by_user',
] as const;
export type PrintFailureCode = (typeof PRINT_FAILURE_CODES)[number];

/** Human-readable text for a failure code, for list rows and messages. */
export const PRINT_FAILURE_LABELS: Readonly<Record<PrintFailureCode, string>> = {
  print_not_configured: 'Print service not configured',
  relay_unreachable: 'Print relay unreachable',
  relay_error: 'Printer rejected the job',
  printer_not_ready: 'Printer could not print (check media, lid and pause)',
  ack_exhausted: 'Printer did not confirm after retries',
  ack_timeout: 'Printer did not respond in time',
  db_error: 'Database error while printing',
  validation_error: 'Invalid print request',
  abandoned: 'Print was interrupted',
  cancelled_by_user: 'Cancelled by user',
} as const;

/** One physical label waiting in (or consumed from) the queue. */
export interface PrintQueueItem {
  readonly id: string;
  readonly orderId: string;
  readonly seq: number;
  readonly sheetSeq: number;
  readonly sku: string | null;
  readonly shelfIndex: string | null;
  readonly baseQty: number | null;
  readonly storageId: string | null;
  readonly storageIdPrefix: string | null;
  readonly status: PrintQueueItemStatus;
  readonly printedAt: string | null;
  readonly failureReason: string | null;
}

/** One composed ZPL sheet. The UI sends every remaining sheet as one relay job. */
export interface PrintOrderSheet {
  readonly sheetSeq: number;
  readonly items: readonly PrintQueueItem[];
}

export interface PrintOrder {
  readonly id: string;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly createdByName: string | null;
  readonly labelKind: PrintLabelKind;
  readonly labelType: LabelType | null;
  readonly source: PrintOrderSource;
  readonly skus: readonly string[];
  readonly shelfIndex: string | null;
  readonly shelfIndexes: readonly string[];
  readonly baseQty: number | null;
  readonly uniqueIdCount: number;
  readonly copiesPerId: number;
  readonly totalLabels: number;
  readonly printedLabels: number;
  readonly status: PrintOrderStatus;
  readonly success: boolean;
  readonly failureCode: PrintFailureCode | null;
  readonly failureReason: string | null;
  readonly retryOfOrderId: string | null;
}

/** An order plus the sheet plan the client walks through. */
export interface PrintOrderWithSheets {
  readonly order: PrintOrder;
  readonly sheets: readonly PrintOrderSheet[];
}

/** Row shape for the Print Log list. `unprintedLabels` drives the retry chip. */
export interface PrintOrderListRow {
  readonly id: string;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly createdByName: string | null;
  readonly labelKind: PrintLabelKind;
  readonly uniqueIdCount: number;
  readonly totalLabels: number;
  readonly printedLabels: number;
  readonly unprintedLabels: number;
  readonly status: PrintOrderStatus;
  readonly success: boolean;
  readonly failureCode: PrintFailureCode | null;
  readonly failureReason: string | null;
}

/**
 * One unique ID's worth of labels — a SKU (stock / pick bin / big) or a shelf index.
 * `copiesPerId` on the order decides how many physical labels each unit produces.
 */
export interface PrintOrderUnitInput {
  readonly sku?: string | null;
  readonly shelfIndex?: string | null;
  readonly baseQty?: number | null;
  /** Reprints and balance retries reuse an existing storage id instead of reserving one. */
  readonly storageId?: string | null;
}

/** Body accepted by POST /api/print-orders. */
export interface CreatePrintOrderInput {
  readonly labelKind: PrintLabelKind;
  readonly labelType?: LabelType | null;
  readonly source: PrintOrderSource;
  /** One entry per unique ID (batch prints list one entry per batch item). */
  readonly units: readonly PrintOrderUnitInput[];
  /** Copies of each unique ID. Always 1 for shelf labels. */
  readonly copiesPerId?: number;
}

/** Max labels a single order may hold — guards a mistyped shelf range. */
export const PRINT_ORDER_MAX_LABELS = 600;
/** Max copies of one unique ID. */
export const PRINT_ORDER_MAX_COPIES = 10;

/** Labels per relay sheet, by label kind. Mirrors the zplGenerator sheet limits. */
export const PRINT_LABELS_PER_SHEET: Readonly<Record<PrintLabelKind, number>> = {
  warehouse: 4,
  big: 1,
  shelf: 6,
  reprint_warehouse: 4,
  reprint_big: 1,
  ship_label: 1,
  order_qr: 1,
} as const;
